import { useCallback, useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import {
    DEFAULT_MEDIA_FALLBACK,
    resolveBundledMedia,
} from '@/lib/media-fallback';

export interface MediaImageProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> {
    /** Remote or relative image source. Null/undefined renders nothing. */
    src?: string | null;
    /** Bundled fallback used when `src` fails to load. */
    fallbackSrc?: string;
}

/**
 * <img> wrapper that never shows a broken image:
 * 1. Known backend R2 keys are resolved to bundled local assets up front.
 * 2. Any remaining load failure swaps to a bundled placeholder via onError.
 */
export function MediaImage({
    src,
    fallbackSrc = DEFAULT_MEDIA_FALLBACK,
    className,
    alt = '',
    fetchPriority: _fetchPriority,
    ...rest
}: MediaImageProps) {
    const [current, setCurrent] = useState<string | undefined>(() =>
        resolveBundledMedia(src) ?? src ?? undefined
    );
    const [triedFallback, setTriedFallback] = useState(false);

    useEffect(() => {
        setCurrent(resolveBundledMedia(src) ?? src ?? undefined);
        setTriedFallback(false);
    }, [src]);

    const handleError = useCallback(() => {
        if (triedFallback) return;
        setTriedFallback(true);
        setCurrent(fallbackSrc);
    }, [fallbackSrc, triedFallback]);

    if (!current) return null;

    return (
        <img
            src={current}
            alt={alt}
            className={cn('select-none', className)}
            onError={handleError}
            {...rest}
            {...(_fetchPriority ? { fetchpriority: _fetchPriority } : {})}
        />
    );
}
